//@flow

import {
  endpoints,
  getConversations,
  getRavenAccount,
  getDevices,
  defaultPagination,
  getDeviceStatus,
  removeDeviceApi,
  reconnectDeviceApi,
  getConversationCountApi,
  createMetaAccountApi,
  connectWhatsappWebDevice,
  getInstagramAccountsApi,
  getFbMessengerAccountsApi,
  connectInstagramDeviceApi,
  connectFbMessengerDeviceApi,
  deleteMetaUserApi,
  getMetaUsersApi,
  removeDeviceWithoutHistoryApi,
  getWhatsappApiTemplatesApi,
  getWhatsappApiAccountsApi,
  connectWhatsappApiDeviceApi,
  getChatbotByIdApi,
  editChatbotApi,
  setChatDeviceBehaviorApi,
  setChatMessageBehaviorApi,
} from '../api';

import {buildRoute} from '../../../../http';

import type {
  PaginationRequests,
  GetDevicesResponsePagination,
  GetRavenAccountResponse,
  GetConversationCountResponse,
} from '../api';

import type {
  Conversation,
  PaginationResponse,
  ConversationState,
  DeviceState,
  GetConversationCountParams,
  MetaUserResponse,
  MetaUserRequest,
  MetaAccountResponse,
  ChatServiceType,
  ConversationResponse,
  ChatBot,
} from '../../../../entities';
import type {
  WhatsAppTemplateListResponse,
  WhatsAppApiAccount,
} from '../../../../entities/multichat/whatsapp-api';
import {conversationAdapter} from '../utils/conversation-adapter';

export * from './conversationCollaborator';

type ConnectWhatsappWebDeviceResponse = {
  qrCode: string,
};

export function connectWhatsappWebDeviceService(
  phone: string
): Promise<ConnectWhatsappWebDeviceResponse> {
  return connectWhatsappWebDevice({phone}).toPromise();
}

export function getRavenAccountService(): Promise<GetRavenAccountResponse> {
  return getRavenAccount().toPromise();
}

export function createMetaAccountService(
  request: MetaUserRequest
): Promise<MetaUserResponse> {
  return createMetaAccountApi(request).toPromise();
}

export function getDevicesService(
  services: ChatServiceType[] = [],
  pagination: PaginationRequests = defaultPagination
): Promise<GetDevicesResponsePagination> {
  return getDevices({
    services: services.join(','),
    size: pagination.size.toString(),
    page: pagination.page.toString(),
  }).toPromise();
}

export function getInstagramAccountsService(
  userId: string
): Promise<MetaAccountResponse> {
  const route = buildRoute(endpoints.GET_INSTAGRAM_ACCOUNTS, [userId]);
  return getInstagramAccountsApi(undefined, {route}).toPromise();
}

export function getFbMessengerAccountsService(
  userId: string
): Promise<MetaAccountResponse> {
  const route = buildRoute(endpoints.GET_FB_MESSENGER_ACCOUNTS, [userId]);
  return getFbMessengerAccountsApi(undefined, {route}).toPromise();
}

type ConnectInstagramDeviceRequest = {
  instagramAccountId: string,
  facebookMetaUserId: string,
};

export function connectInstagramDeviceService(
  request: ConnectInstagramDeviceRequest
): Promise<void> {
  return connectInstagramDeviceApi(request).toPromise();
}

type ConnectFbMessengerDeviceRequest = {
  fbMessengerAccountId: string,
  facebookMetaUserId: string,
};

export function connectFbMessengerDeviceService(
  request: ConnectFbMessengerDeviceRequest
): Promise<void> {
  return connectFbMessengerDeviceApi(request).toPromise();
}

export type GetConversationsOptions = {
  pagination?: PaginationRequests,
  tags?: string[],
  state?: ConversationState,
  identifier?: string,
  admin?: boolean,
  collaborators?: string[],
  scheduleMessageState?: string,
  requiresAssistance?: boolean,
  deviceId?: string,
};

export function getConversationsService(
  services: ChatServiceType[] = [],
  options: GetConversationsOptions = {}
): Promise<PaginationResponse<Conversation>> {
  const {
    tags,
    state,
    identifier,
    admin,
    collaborators,
    scheduleMessageState,
    requiresAssistance,
    deviceId,
  } = options;

  const pagination = options.pagination || defaultPagination;

  const payload: any = {
    services: services.join(','),
    size: pagination.size.toString(),
    page: pagination.page.toString(),
  };

  if (admin) {
    payload.admin = Number(admin);
  }

  if (options.requiresAssistance) {
    payload.requires_assistance = Number(requiresAssistance);
  }

  if (identifier) {
    payload.identifier = identifier;
  }

  if (state === 'ALL') {
    payload.state = undefined;
  } else {
    payload.state = state;
  }

  if (scheduleMessageState) {
    payload.scheduled_message_states = scheduleMessageState;
  }

  if (Array.isArray(tags) && tags.length > 0) {
    payload.tags = tags.join(',');
  }

  if (Array.isArray(collaborators) && collaborators.length > 0) {
    payload.collaborators = collaborators.join(',');
  }

  // deviceId must be sent with a single service (enforced by filter UI)
  if (deviceId && services.length === 1) {
    payload.device_id = deviceId;
  }

  return getConversations(payload)
    .toPromise()
    .then((response: PaginationResponse<ConversationResponse>) => {
      return {
        ...response,
        data: response.data.map((conversation: ConversationResponse) => {
          return conversationAdapter(conversation);
        }),
      };
    });
}

export function getDeviceStatusService(
  deviceId: string,
  service: ChatServiceType
): Promise<{status: DeviceState}> {
  const route = buildRoute(endpoints.GET_DEVICE_STATUS, [deviceId]);

  return getDeviceStatus({service}, {route}).toPromise();
}

export function removeDeviceService(
  deviceId: string,
  service: ChatServiceType
): Promise<void> {
  const route = buildRoute(endpoints.REMOVE_DEVICE, [deviceId]);

  return removeDeviceApi({
    route: `${route}?service=${service}&hard=1`,
  }).toPromise();
}

export function removeDeviceWithoutHistoryService(
  deviceId: string,
  service: ChatServiceType
): Promise<void> {
  const route = buildRoute(endpoints.REMOVE_DEVICE_WITHOUT_HISTORY, [deviceId]);
  return removeDeviceWithoutHistoryApi({
    route: `${route}?service=${service}`,
  }).toPromise();
}

export function reconnectDeviceService(
  deviceId: string,
  service: ChatServiceType
): Promise<void> {
  const route = buildRoute(endpoints.RECONNECT_DEVICE, [deviceId]);
  return reconnectDeviceApi(undefined, {route: `${route}`}).toPromise();
}

export function deleteMetaUserService(userId: string) {
  const route = buildRoute(endpoints.DELETE_META_USER, [userId]);

  return deleteMetaUserApi({route}).toPromise();
}

export function getMetaUsersService(): Promise<MetaUserResponse> {
  return getMetaUsersApi().toPromise();
}

export function getConversationCountService(
  params: GetConversationCountParams
): Promise<GetConversationCountResponse> {
  const {admin, collaborators, services, state, tags} = params;

  const queryParams: any = {
    services: services.join(','),
  };

  if (collaborators && collaborators.length > 0) {
    queryParams.collaborators = collaborators.join(',');
  }

  if (admin) {
    queryParams.admin = admin;
  }

  if (state && state !== 'ALL') {
    queryParams.state = state;
  }

  if (tags && tags.length > 0) {
    queryParams.tags = tags.join(',');
  }

  return getConversationCountApi(queryParams).toPromise();
}

export function getWhatsappApiTemplatesService(
  deviceId: string
): Promise<WhatsAppTemplateListResponse> {
  const route = buildRoute(endpoints.GET_WHATSAPP_API_TEMPLATES, [deviceId]);
  return getWhatsappApiTemplatesApi(undefined, {route}).toPromise();
}

export function getWhatsappApiAccountsService(): Promise<{
  accounts: WhatsAppApiAccount[],
}> {
  return getWhatsappApiAccountsApi().toPromise();
}

export function connectWhatsappApiDeviceService(
  accountId: string
): Promise<void> {
  return connectWhatsappApiDeviceApi({
    whatsappApiAccountId: accountId,
  }).toPromise();
}

export function getChatbotByIdService(chatbotId: string): Promise<ChatBot> {
  const route = buildRoute(endpoints.GET_CHATBOT_BY_ID, [chatbotId]);
  return getChatbotByIdApi(undefined, {route}).toPromise();
}

type EditChatbotRequest = {
  enabled?: boolean,
  name?: string,
};

export function editChatbotService(
  chatbotId: string,
  data: EditChatbotRequest
): Promise<ChatBot> {
  const route = buildRoute(endpoints.EDIT_CHATBOT, [chatbotId]);
  return editChatbotApi(data, {route}).toPromise();
}

type DeviceBehaviorRequest = {
  service: ChatServiceType,
  deviceId: string,
  behavior: 'AVAILABLE' | 'UNAVAILABLE',
};

export function setChatDeviceBehaviorService(
  deviceId: string,
  data: DeviceBehaviorRequest
): Promise<void> {
  const route = buildRoute(endpoints.CHAT_DEVICE_BEHAVIOR, [deviceId]);
  return setChatDeviceBehaviorApi(data, {route}).toPromise();
}

type MessageBehaviorRequest = {
  service: ChatServiceType,
  deviceId: string,
  behavior: 'COMPOSING' | 'PAUSED',
};

export function setChatMessageBehaviorService(
  conversationId: string,
  data: MessageBehaviorRequest
): Promise<void> {
  const route = buildRoute(endpoints.CHAT_MESSAGE_BEHAVIOR, [conversationId]);
  return setChatMessageBehaviorApi(data, {route}).toPromise();
}
